import { faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { Badge, Breadcrumbs, Popover, Stack } from '@mantine/core'
import Link from 'next/link'
import { useState } from 'react'

import { Icon, useMediaQuery } from '@flecto/ui'

import classes from './styles.module.css'

export type BreadcrumbItem = {
  label: string
  path: string
}

export function PageBreadcrumb({ data }: { data: BreadcrumbItem[] }) {
  const { isMobile } = useMediaQuery()
  const [opened, setOpened] = useState(false)

  const renderBreadcrumbItem = (item: BreadcrumbItem, index: number) => (
    <Link
      key={item.path}
      href={item.path}
      className={classes.pageBreadcrumbLink}
      data-testid={`breadcrumb-link-${index}`}
    >
      <Badge
        variant='light'
        color={index === data.length - 1 ? 'gray.9' : 'gray.6'}
        radius='sm'
        styles={{
          root: {
            padding: '4px 8px',
            backgroundColor:
              index === data.length - 1
                ? 'var(--mantine-color-gray-2)'
                : '#FFFFFF',
            textDecoration: 'none',
            transition: 'background-color 0.2s ease',
          },
          label: {
            fontSize: '0.75rem',
            fontWeight: 500,
            color:
              index === data.length - 1
                ? 'var(--mantine-color-gray-9)'
                : 'var(--mantine-color-gray-6)',
            textTransform: 'none',
          },
        }}
        className={
          index !== data.length - 1
            ? classes.pageBreadcrumbBadgeRoot
            : undefined
        }
        data-testid={`breadcrumb-item-${index}`}
      >
        {item.label}
      </Badge>
    </Link>
  )

  const separator = <Icon size='sm' icon={faChevronRight} color='gray.5' />

  const items =
    isMobile && data.length > 2 ? (
      <>
        {renderBreadcrumbItem(data[0], 0)}
        {separator}
        <Popover
          position='bottom'
          shadow='md'
          opened={opened}
          onChange={setOpened}
        >
          <Popover.Target>
            <Badge
              variant='light'
              color='gray.6'
              radius='sm'
              styles={{
                root: {
                  padding: '4px 8px',
                  backgroundColor: '#FFFFFF',
                  textDecoration: 'none',
                  transition: 'background-color 0.2s ease',
                  cursor: 'pointer',
                  maxWidth: '97svw',
                  overflow: 'hidden',
                },
                label: {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textTransform: 'none',
                },
              }}
              onClick={() => setOpened((o) => !o)}
              data-testid='breadcrumb-ellipsis'
              className={classes.pageBreadcrumbBadgeRoot}
            >
              ...
            </Badge>
          </Popover.Target>
          <Popover.Dropdown>
            <Stack gap='sm'>
              {data.slice(1, -1).map((item, index) => (
                <Link
                  key={item.path}
                  href={item.path}
                  className={classes.pageBreadcrumbLinkDropdown}
                  data-testid={`breadcrumb-link-hidden-${index + 1}`}
                  onClick={() => setOpened(false)}
                >
                  {item.label}
                </Link>
              ))}
            </Stack>
          </Popover.Dropdown>
        </Popover>
        {separator}
        {renderBreadcrumbItem(data[data.length - 1], data.length - 1)}
      </>
    ) : (
      data.map((item, index) => (
        <div key={item.path}>{renderBreadcrumbItem(item, index)}</div>
      ))
    )

  return (
    <Breadcrumbs
      className={classes.pageBreadcrumb}
      separator={separator}
      styles={{
        root: {
          marginBottom: '1.5rem',
        },
      }}
      data-testid='page-breadcrumb'
    >
      {items}
    </Breadcrumbs>
  )
}

PageBreadcrumb.displayName = 'PageBreadcrumb'
